import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Divider,
  FormControl,
  Grid,
  Icon,
  IconButton,
  InputLabel,
  makeStyles,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { isEmpty } from "lodash";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useDispatch } from "react-redux";
import { utils, writeFileXLSX } from "xlsx";
import { dateDef } from "../../../../config/datetime";
import { setPageLoading, setPanelTitle } from "../../../../config/redux";
import { DeleteTrackData, GetTracks } from "../../../../config/services";
import Confirm from "../../../components/Confirm/Confirm";
import FormSearchPanel from "../../../components/Forms/FormSearchPanel";
import TableData from "../../../components/Panel/Table";
import Create from "./Create";
import Edit from "./Edit";
import View from "./View";

const Row = ({
  track,
  handleMoreOpen,
  anchorEl,
  moreOpen,
  handleCloseMore,
  clickMoreItem,
}) => {
  const [open, setOpen] = React.useState(false);

  return (
    <React.Fragment>
      <TableRow key={track.id} hover>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? (
              <Icon>keyboard_arrow_up</Icon>
            ) : (
              <Icon>keyboard_arrow_down</Icon>
            )}
          </IconButton>
        </TableCell>
        <TableCell>{track.id}</TableCell>
        <TableCell>
          {track.gate_in.name} ({track.gate_in.id})
        </TableCell>
        <TableCell>
          {track.gate_out.name} ({track.gate_out.id})
        </TableCell>
        <TableCell>{track.description ?? "-"}</TableCell>
        <TableCell>{dateDef(track.created_at)}</TableCell>
        <TableCell>
          <IconButton
            size="small"
            onClick={(event) => handleMoreOpen(event, track.id)}
          >
            <Icon>more_vert</Icon>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            keepMounted
            open={moreOpen === track.id ? true : false}
            onClose={handleCloseMore}
          >
            {moreItems.map((item) => (
              <MenuItem key={item} onClick={() => clickMoreItem(item, track)}>
                {item}
              </MenuItem>
            ))}
          </Menu>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom component="div">
                Tarif Toll
              </Typography>
              <Table size="small" aria-label="rates">
                <TableHead>
                  <TableRow>
                    <TableCell>Nama</TableCell>
                    <TableCell>Harga</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {track.rates.map((rate, index) => (
                    <TableRow key={index}>
                      <TableCell component="th" scope="row">
                        {rate.name}
                      </TableCell>
                      <TableCell>{rate.price}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

const useStyles = makeStyles((theme) => ({
  grow: {
    flex: 1,
  },
  select: {
    minWidth: 250,
  },
  cardRoot: {
    marginTop: 25,
    marginBottom: 25,
  },
  ...theme.styles.panelCard,
}));

const columns = [
  {},
  {
    id: "id",
    label: "ID",
  },
  {
    id: "gate_in",
    label: "Gerbang Masuk",
    minWidth: 200,
  },
  {
    id: "gate_out",
    label: "Gerbang Keluar",
    minWidth: 200,
  },
  {
    id: "description",
    label: "Deskripsi",
    minWidth: 250,
  },
  {
    id: "created_at",
    label: "Dibuat",
    minWidth: 200,
  },
  {
    id: "action",
    label: "Action",
    minWidth: 100,
  },
];

const moreItems = ["Edit", "View", "Delete"];

function Tracks() {
  const [loading, setLoading] = useState(0);
  const classes = useStyles();
  const [search, setSearch] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [data, setData] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [trackDelete, setTrackDelete] = useState(null);
  const [trackDeleteLoading, setTrackDeleteLoading] = useState(false);
  const [editTrack, setEditTrack] = useState({});
  const [viewTrack, setViewTrack] = useState({});
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [filter, setFilter] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    fetchTracks();
    dispatch(setPanelTitle("Jalur Toll"));
  }, []);

  const fetchTracks = async (params) => {
    setLoading((prev) => prev + 1);
    try {
      const response = await GetTracks(params);
      setData(response.data.tracks);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading((prev) => prev - 1);
    }
  };

  const handleChangeSearch = (event) => {
    setSearch(event.target.value);
  };

  const onSubmitSearch = (event) => {
    if (event.nativeEvent.key === "Enter") {
      fetchTracks({ q: search, segment_id: filter });
    }
  };

  const clearSearchInput = () => {
    if (search) {
      setSearch("");
      fetchTracks();
    }
  };

  const dialogCreateOpen = () => {
    setCreateDialogOpen(true);
  };

  const handleChangePage = (page) => {
    fetchTracks({
      page: page,
      limit: data.meta.per_page,
      segment_id: filter,
    });
  };

  const handleChangeRowsPerPage = (limit) => {
    fetchTracks({ limit: limit, segment_id: filter });
  };

  const handleMoreOpen = (event, id) => {
    setAnchorEl(event.currentTarget);
    setMoreOpen(id);
  };

  const handleCloseMore = () => {
    setAnchorEl(null);
    setMoreOpen(false);
  };

  const clickMoreItem = (item, segment) => {
    if (item === "Edit") {
      setEditTrack(segment);
    } else if (item === "Delete") {
      setOpenConfirmDelete(true);
      setTrackDelete(segment.id);
    } else {
      setViewTrack(segment);
    }
    handleCloseMore();
  };

  const handleDelete = async (type) => {
    if (type) {
      setTrackDeleteLoading(true);
      try {
        const response = await DeleteTrackData(trackDelete);
        fetchTracks();
        setTrackDelete(null);
        setOpenConfirmDelete(false);
      } catch (error) {
        console.log(error);
      } finally {
        setTrackDeleteLoading(false);
      }
    } else {
      setOpenConfirmDelete(false);
    }
  };

  const handleChangeFilter = (event) => {
    setFilter(event.target.value);
    fetchTracks({ segment_id: event.target.value, q: search });
  };

  const handleExport = async () => {
    try {
      setLoadingExport(true);
      dispatch(setPageLoading(true));

      const response = await GetTracks({
        paginate: false,
      });
      const result = response.data.tracks.map((track) => {
        let data = {
          ID: track.id,
          "Gerbang Masuk": track.gate_in.name,
          "Gerbang Keluar": track.gate_out.name,
        };
        track.rates.forEach((rate) => {
          data[rate.name] = rate.price;
        });
        return data;
      });
      const ws = utils.json_to_sheet(result);
      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws, "Data");
      writeFileXLSX(wb, "Data-Jalur-Toll.xlsx");
    } catch (error) {
      console.log(error);
    } finally {
      dispatch(setPageLoading(false));
      setLoadingExport(false);
    }
  };

  return (
    <div>
      <Helmet>
        <title>Jalur Toll</title>
      </Helmet>
      <Grid container spacing={3} alignItems="flex-end">
        <Grid className={classes.grow} />
        <Grid item>
          <FormSearchPanel
            value={search}
            onChange={handleChangeSearch}
            onKeyPress={onSubmitSearch}
            clearSearchInput={clearSearchInput}
          />
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            color="default"
            onClick={dialogCreateOpen}
          >
            Buat Jalur Toll
          </Button>
        </Grid>
      </Grid>
      <Card className={classes.cardRoot}>
        <CardHeader
          title="Data Jalur Toll"
          titleTypographyProps={{
            className: classes.cardTitle,
          }}
          action={
            <Button
              variant="contained"
              color="primary"
              onClick={handleExport}
              disabled={loadingExport}
            >
              Export Data
            </Button>
          }
        />
        <Divider />
        <CardContent className={classes.cardContent}>
          <TableData
            columns={columns}
            meta={{ ...data.meta, ...data.links }}
            loading={loading > 0}
            onHandleChangePage={(page) => handleChangePage(page)}
            onHandleChangeRowsPerPage={(limit) =>
              handleChangeRowsPerPage(limit)
            }
          >
            {data.data?.map((track) => (
              <Row
                key={track.id}
                track={track}
                handleMoreOpen={handleMoreOpen}
                anchorEl={anchorEl}
                moreOpen={moreOpen}
                handleCloseMore={handleCloseMore}
                clickMoreItem={clickMoreItem}
              />
            ))}
          </TableData>
        </CardContent>
      </Card>
      {createDialogOpen && (
        <Create
          fetchTracks={fetchTracks}
          createDialogOpen={createDialogOpen}
          setCreateDialogOpen={setCreateDialogOpen}
        />
      )}
      {!isEmpty(editTrack) && (
        <Edit
          fetchTracks={fetchTracks}
          open={!isEmpty(editTrack)}
          editTrack={editTrack}
          setEditTrack={setEditTrack}
        />
      )}

      {!isEmpty(viewTrack) && (
        <View
          open={!isEmpty(viewTrack)}
          viewTrack={viewTrack}
          setViewTrack={setViewTrack}
        />
      )}
      {openConfirmDelete && (
        <Confirm
          loading={trackDeleteLoading}
          open={openConfirmDelete}
          content="Anda yakin ingin menghapus data ?"
          onAction={(type) => handleDelete(type)}
        />
      )}
    </div>
  );
}

export default Tracks;
